"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AdvancesList, type AdvanceFilters } from "./list"
import axiosInstance from "../../../libs/axios"

import { useUrlFilters } from "../../../hooks/useUrlFilters";
import { URL_FILTER_KEYS } from "./list";
import { useAuth } from "../../../provider/auth.provider"
import { useDebounce } from "../../../hooks/useDebounce"

interface RestrictionStatus {
  is_blocked: boolean
  block_reason: string | null
  limit: { max_amount: number; reason: string | null } | null
  /** Whether this role is subject to the month's site-work rule at all. */
  requires_site_activity?: boolean
  /** False only when the rule applies AND the user has done no site work. */
  has_site_activity_this_month?: boolean
  site_activity_reason?: string | null
}

const AdvancesClaim: React.FC = () => {
  const { hasPermission } = useAuth()
  const canViewAllAdvances = hasPermission("advance_claim", "view_all")
  const [advances, setAdvances] = useState([])
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  // Seeded from the URL so a dashboard tile can open this list already
  // filtered to the rows it counted. URL_FILTER_KEYS lives in ./list.
  const [advancedFilters, setAdvancedFilters] = useState<AdvanceFilters>(
    useUrlFilters<AdvanceFilters>({}, URL_FILTER_KEYS),
  )
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [restrictionStatus, setRestrictionStatus] = useState<RestrictionStatus>({
    is_blocked: false,
    block_reason: null,
    limit: null,
    requires_site_activity: false,
    has_site_activity_this_month: true,
    site_activity_reason: null,
  })

  // A site engineer who has done no site work on the portal this month cannot
  // raise an advance — there is nothing for it to fund. The API refuses the
  // request either way; folding it into the same blocked state the list
  // already renders means the form says so before it is filled in.
  const isAdvanceBlocked =
    restrictionStatus.is_blocked || restrictionStatus.has_site_activity_this_month === false
  const advanceBlockReason =
    restrictionStatus.block_reason ?? restrictionStatus.site_activity_reason ?? null

  useEffect(() => {
    axiosInstance.get("/claims/advance/restrictions/my-status")
      .then(res => { if (res.data.success) setRestrictionStatus(res.data.data) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (canViewAllAdvances) {
      fetchAdvances()
    }
  }, [canViewAllAdvances])

  const fetchAdvances = async (
    page = pagination.page,
    status = statusFilter,
    search = searchTerm,
    limitValue?: number,
    filters = advancedFilters,
    sort = sortConfig
  ) => {
    if (!canViewAllAdvances) return

    try {
      setLoading(true)
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const transformedFilters = { ...filters }

      if (transformedFilters.created_from) {
        transformedFilters.created_at_from = transformedFilters.created_from
        delete transformedFilters.created_from
      }

      if (transformedFilters.created_to) {
        transformedFilters.created_at_to = transformedFilters.created_to
        delete transformedFilters.created_to
      }

      const params: any = {
        page: page,
        limit: effectiveLimit,
        ...transformedFilters
      }

      if (status) params.status = status
      if (search) params.search = search
      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await axiosInstance.get("/claims/advance/list", { params })

      if (response.data.success) {
        setAdvances(response.data.data.advances)
        setPagination(response.data.data.pagination)
      }
    } catch (error) {
      console.error("Error fetching advances:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    if (!canViewAllAdvances) return
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    if (!canViewAllAdvances) return
    fetchAdvances(1, statusFilter, debouncedSearch, undefined, advancedFilters, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const getDefaultSortForStatus = (status: string): { field: string; direction: "ASC" | "DESC" } => {
    if (status === "approved") return { field: "approved_date", direction: "ASC" }
    if (status === "transferred" || status === "completed") return { field: "transferred_date", direction: "DESC" }
    // requested or empty — oldest first
    return { field: "created_at", direction: "ASC" }
  }

  const handleFilterChange = (filter: string) => {
    if (!canViewAllAdvances) return
    setStatusFilter(filter)
    const newSort = getDefaultSortForStatus(filter)
    setSortConfig(newSort)
    fetchAdvances(1, filter, searchTerm, undefined, advancedFilters, newSort)
  }

  const handleAdvancedFilter = (filters: AdvanceFilters) => {
    if (!canViewAllAdvances) return
    setAdvancedFilters(filters)
    fetchAdvances(1, statusFilter, searchTerm, undefined, filters, sortConfig)
  }

  const handlePageChange = (page: number) => {
    if (!canViewAllAdvances) return
    fetchAdvances(page, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    if (!canViewAllAdvances) return
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchAdvances(1, statusFilter, searchTerm, limit, advancedFilters, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    if (!canViewAllAdvances) return
    const sort = { field, direction }
    setSortConfig(sort)
    fetchAdvances(pagination.page, statusFilter, searchTerm, undefined, advancedFilters, sort)
  }

  const handleRefresh = () => {
    if (!canViewAllAdvances) return
    fetchAdvances(pagination.page, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  return (
    <div>
      <AdvancesList
        advances={advances}
        pagination={pagination}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdvancedFilter={handleAdvancedFilter}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        appliedFilters={advancedFilters}
        searchValue={searchTerm}
        onRefresh={handleRefresh}
        onAdvanceCreated={() => fetchAdvances(1, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)}
        canViewAllAdvances={canViewAllAdvances}
        isBlocked={isAdvanceBlocked}
        blockReason={advanceBlockReason}
        userLimit={restrictionStatus.limit}
      />
    </div>
  )
}

export default AdvancesClaim
